import { ACADEMIC_STATUS_MAP } from 'constants/academic-status-map';
import {
  AcademicHistoryData,
  fetchAcademicHistoryData
} from 'services/graduate-enrollment/academic-history';
import { Button, Empty, Spin, Table, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { IAcademicRecord } from 'interfaces/academic_records';
import { Link } from 'react-router-dom';
import { TagField, TextField } from '@refinedev/antd';
import { TitleMap } from 'services/professors';
import {
  disciplineModulesClassification,
  disciplineModulesKind
} from 'pages/graduate_courses/components/TabDisciplines';
import { formatDate } from 'services/date';
import { useCallback, useEffect, useState } from 'react';
import { useNotification } from '@refinedev/core';

interface AcademicHistoryProps {
  courseId: string;
  enrollmentId: string;
  isUndergraduate: boolean;
}

export const TabAcademicHistory = ({
  courseId,
  enrollmentId,
  isUndergraduate
}: AcademicHistoryProps) => {
  const [data, setData] = useState<AcademicHistoryData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { open } = useNotification();

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetchAcademicHistoryData(courseId, enrollmentId);
      setData(res);
    } catch {
      open?.({
        type: 'error',
        message: 'Erro ao recuperar informações do histórico acadêmico.',
        description: 'Ops!'
      });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, enrollmentId, open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Spin />
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nenhum histórico acadêmico encontrado"
      />
    );
  }

  return (
    <div>
      <Typography.Text type="secondary" className="block mb-4">
        Listagem de disciplinas concluídas.
      </Typography.Text>

      <Table
        dataSource={data}
        className="w-full"
        size="small"
        style={{ borderRadius: 10, overflow: 'hidden' }}
        pagination={false}
      >
        <Table.Column
          title="Disciplina"
          dataIndex="discipline"
          key="discipline"
          className="w-[300px]"
          render={value => <TextField value={value?.name} />}
        />
        <Table.Column
          title="Tipo"
          dataIndex={['disciplineModule']}
          key="kind"
          render={value => (
            <TextField
              value={
                disciplineModulesKind[
                  value?.kind as keyof typeof disciplineModulesKind
                ]
              }
            />
          )}
        />
        {isUndergraduate && (
          <Table.Column
            title="Classificação"
            dataIndex={['disciplineModule', 'classification']}
            key="classification"
            width={130}
            render={value => (
              <TextField
                value={
                  disciplineModulesClassification[
                    value as keyof typeof disciplineModulesClassification
                  ] ?? '-'
                }
              />
            )}
          />
        )}
        <Table.Column
          title="Professor"
          dataIndex="professor"
          key="name"
          render={value => (
            <>
              {value && (
                <>
                  <TextField value={value?.name} />
                  <TextField
                    type="secondary"
                    value={` (${
                      TitleMap[value?.title as keyof typeof TitleMap]
                    })`}
                  />
                </>
              )}
            </>
          )}
        />
        <Table.Column
          title="Turma"
          dataIndex="parent"
          key="parent"
          render={value => <TextField value={value?.classroom?.code} />}
        />

        <Table.Column
          title="Vigência"
          dataIndex="parent"
          key="parent"
          className="min-w-[140px] max-w-[140px]"
          render={value => (
            <TextField
              value={
                <>
                  {formatDate(value?.classroom?.starts_at, 'DD/MM/YYYY')} -{' '}
                  {formatDate(value?.classroom?.ends_at, 'DD/MM/YYYY')}
                </>
              }
            />
          )}
        />
        <Table.Column
          title="Frequência"
          dataIndex="attendance_rate"
          key="attendance_rate"
          align="center"
          render={value => (
            <TextField value={value == null ? 'Sem frequência' : `${value}%`} />
          )}
        />
        <Table.Column
          title="Nota"
          dataIndex="score"
          key="score"
          align="center"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          title="Status"
          dataIndex="status"
          key="status"
          className="w-[80px]"
          render={(value: IAcademicRecord['status']) => (
            <TagField
              color={ACADEMIC_STATUS_MAP[value].color}
              value={ACADEMIC_STATUS_MAP[value].label}
            />
          )}
        />
        <Table.Column<AcademicHistoryData>
          title="Ações"
          dataIndex="actions"
          key="actions"
          align="center"
          width={80}
          render={(_, record) => (
            <Link to={`/secretary/graduate/academic_records/show/${record.id}`}>
              <Button size="small" icon={<EyeOutlined />} />
            </Link>
          )}
        />
      </Table>
    </div>
  );
};
